import React, { useState, useRef, useEffect } from 'react';
import { Input } from './input';
import { Calendar } from 'lucide-react';
import { formatDateDDMMYYYY, convertToYYYYMMDD } from '../../utils/dateFormat';

/**
 * Custom Date Input Component
 * Displays dates in dd/mm/yyyy format (Indian format)
 * Uses HTML5 date input internally but displays formatted date
 */
const DateInput = ({ 
  value, 
  onChange, 
  min, 
  max, 
  required, 
  className = '', 
  id,
  placeholder = 'dd/mm/yyyy',
  disabled = false
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const dateInputRef = useRef(null);
  const wrapperRef = useRef(null);

  // Convert YYYY-MM-DD to dd/mm/yyyy for display
  useEffect(() => {
    if (value) {
      const formatted = formatDateDDMMYYYY(value);
      setDisplayValue(formatted);
    } else {
      setDisplayValue('');
    }
  }, [value]);

  // Handle display input change (user typing dd/mm/yyyy)
  const handleDisplayChange = (e) => {
    let inputValue = e.target.value;
    
    // Remove non-numeric characters except /
    inputValue = inputValue.replace(/[^\d/]/g, '');
    
    // Auto-format as user types
    let formatted = inputValue;
    if (inputValue.length > 2 && !inputValue.includes('/')) {
      formatted = inputValue.slice(0, 2) + '/' + inputValue.slice(2);
    }
    if (formatted.length > 5 && formatted.split('/').length === 2) {
      formatted = formatted.slice(0, 5) + '/' + formatted.slice(5, 9);
    }
    
    // Limit to dd/mm/yyyy format
    if (formatted.length > 10) {
      formatted = formatted.slice(0, 10);
    }
    
    setDisplayValue(formatted);
    
    // Convert to YYYY-MM-DD and validate
    if (formatted.length === 10) {
      const parts = formatted.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        if (day && month && year && day.length === 2 && month.length === 2 && year.length === 4) {
          const yyyyMMdd = convertToYYYYMMDD(formatted);
          if (yyyyMMdd && onChange) {
            onChange({ target: { value: yyyyMMdd } });
          }
        }
      }
    }
  };

  // Handle calendar icon click
  const handleCalendarClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dateInputRef.current && !disabled) {
      // Try to show the native date picker
      if (dateInputRef.current.showPicker) {
        try {
          dateInputRef.current.showPicker();
        } catch (err) {
          // Fallback: focus the hidden input to trigger picker
          dateInputRef.current.focus();
          dateInputRef.current.click();
        }
      } else {
        // Fallback for browsers that don't support showPicker()
        dateInputRef.current.focus();
        dateInputRef.current.click();
      }
    }
  };

  // Handle focus on display input
  const handleDisplayFocus = () => {
    setIsFocused(true);
    // If empty, show placeholder format
    if (!displayValue) {
      setDisplayValue('dd/mm/yyyy');
    }
  };

  // Handle blur on display input
  const handleDisplayBlur = () => {
    setIsFocused(false);
    // Validate and format on blur
    if (displayValue && displayValue !== 'dd/mm/yyyy') {
      const parts = displayValue.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        if (day && month && year && day.length === 2 && month.length === 2 && year.length === 4) {
          const yyyyMMdd = convertToYYYYMMDD(displayValue);
          if (yyyyMMdd) {
            const formatted = formatDateDDMMYYYY(yyyyMMdd);
            setDisplayValue(formatted);
            if (onChange) {
              onChange({ target: { value: yyyyMMdd } });
            }
          }
        } else {
          // Invalid format, reset to value
          if (value) {
            setDisplayValue(formatDateDDMMYYYY(value));
          } else {
            setDisplayValue('');
          }
        }
      } else if (displayValue === 'dd/mm/yyyy') {
        setDisplayValue('');
      }
    }
  };

  // Handle native date input change (when using calendar picker)
  const handleNativeDateChange = (e) => {
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <div className="relative">
      {/* Hidden native date input for calendar picker */}
      <input
        ref={dateInputRef}
        type="date"
        value={value || ''}
        onChange={handleNativeDateChange}
        min={min}
        max={max}
        required={required}
        disabled={disabled}
        className="absolute opacity-0 pointer-events-auto"
        tabIndex={-1}
        aria-label="Date picker (hidden, used for calendar functionality)"
        style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', zIndex: 1, cursor: 'pointer' }}
      />
      
      {/* Display input (dd/mm/yyyy format) */}
      <div className="relative" style={{ zIndex: 2 }}>
        <Input
          id={id}
          type="text"
          value={displayValue}
          onChange={handleDisplayChange}
          onFocus={handleDisplayFocus}
          onBlur={handleDisplayBlur}
          placeholder={placeholder}
          className={className}
          disabled={disabled}
          required={required}
          maxLength={10}
          onClick={(e) => {
            // When clicking on the input, open the date picker
            if (dateInputRef.current && !disabled) {
              e.preventDefault();
              handleCalendarClick(e);
            }
          }}
        />
        <button
          type="button"
          onClick={handleCalendarClick}
          disabled={disabled}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 focus:outline-none disabled:opacity-50 z-10"
          aria-label="Open date picker"
          style={{ pointerEvents: 'auto' }}
        >
          <Calendar className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default DateInput;
